import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Copy, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { ASSESSMENT_TYPES, PEER_CATEGORIES, REVIEW_TYPES } from '@/modules/shared/hr-form-engine';
import { DEFAULT_PM_FORMS_ORDER } from '../../graphql/queries';
import { useDeletePmForm, useGetPmForms } from '../../hooks/use-forms';
import { PmForm } from '../../types/form.types';
import { PageHeader } from '@/components/core';
import { appPath } from '@/constant/app-paths';

const ALL = 'all';

const titleOf = <T extends { key: string; title: string }>(options: T[], key?: string) =>
  options.find((option) => option.key === key)?.title ?? key ?? '-';

export const FormListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_PM_FORMS_ORDER,
  });
  const [assessmentType, setAssessmentType] = useState(ALL);
  const [reviewType, setReviewType] = useState(ALL);
  const [deleteTarget, setDeleteTarget] = useState<PmForm | null>(null);

  const { data, isLoading, error } = useGetPmForms({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(
      {
        ...(assessmentType !== ALL ? { AssessmentType: assessmentType } : {}),
        ...(reviewType !== ALL ? { ReviewType: reviewType } : {}),
      },
      columnWhere
    ),
    order,
  });
  const result = data?.PmForms;

  const { mutateAsync: deletePmForm, isPending: isDeleting } = useDeletePmForm();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deletePmForm(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  const columns: ColumnDef<PmForm>[] = [
    {
      id: 'Title',
      meta: { filter: { type: 'text' } },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('TITLE')} />,
      accessorFn: (row) => row.Title,
    },
    {
      id: 'AssessmentType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('ASSESSMENT_TYPE')} />
      ),
      accessorFn: (row) => t(titleOf(ASSESSMENT_TYPES, row.AssessmentType)),
    },
    {
      id: 'ReviewType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('REVIEW_TYPE')} />,
      accessorFn: (row) => t(titleOf(REVIEW_TYPES, row.ReviewType)),
    },
    {
      id: 'Category',
      meta: {
        filter: {
          type: 'options',
          options: PEER_CATEGORIES.map((category) => ({
            value: category.key,
            label: t(category.title),
          })),
        },
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('CATEGORY')} />,
      accessorFn: (row) => (row.Category ? t(titleOf(PEER_CATEGORIES, row.Category)) : '-'),
    },
    {
      id: 'Departments',
      header: () => <span className="text-xs font-medium">{t('DEPARTMENTS')}</span>,
      enableSorting: false,
      accessorFn: (row) =>
        row.IsAllDepartments ? t('ALL_DEPARTMENTS') : String(row.DepartmentIds?.length ?? 0),
    },
    {
      id: 'Designations',
      header: () => <span className="text-xs font-medium">{t('DESIGNATIONS')}</span>,
      enableSorting: false,
      accessorFn: (row) =>
        row.IsAllDesignations ? t('ALL_DESIGNATIONS') : String(row.DesignationIds?.length ?? 0),
    },
    {
      id: 'actions',
      header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(appPath.reviewTemplate(row.original.ItemId))}
            aria-label={t('EDIT')}
          >
            <PencilSimple className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`${appPath.reviewTemplate(row.original.ItemId)}?duplicate=true`)}
            aria-label={t('DUPLICATE')}
          >
            <Copy className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteTarget(row.original)}
            aria-label={t('DELETE')}
          >
            <Trash className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('REVIEW_FORMS')}
        actions={[
          {
            label: t('CREATE_FORM'),
            icon: <Plus className="size-5" />,
            onClick: () => navigate(appPath.reviewTemplate('new')),
          },
        ]}
      />

      <div className="flex flex-wrap gap-2">
        <Select
          value={assessmentType}
          onValueChange={(value) => {
            setAssessmentType(value);
            resetPage();
          }}
        >
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder={t('ASSESSMENT_TYPE')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ALL_ASSESSMENT_TYPES')}</SelectItem>
            {ASSESSMENT_TYPES.map((type) => (
              <SelectItem key={type.key} value={type.key}>
                {t(type.title)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={reviewType}
          onValueChange={(value) => {
            setReviewType(value);
            resetPage();
          }}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder={t('REVIEW_TYPE')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ALL_REVIEW_TYPES')}</SelectItem>
            {REVIEW_TYPES.map((type) => (
              <SelectItem key={type.key} value={type.key}>
                {t(type.title)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={result?.items ?? []}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: result?.totalCount ?? 0,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('DELETE_FORM')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
